import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { closeUserRemoteSession, loginUser } from "../../../apis/auth/authApi";
import {
  signInWithFacebookAndGetIdToken,
  signInWithGoogleAndGetIdToken,
  signInWithAppleAndGetIdToken,
  signOutFirebaseAuth,
} from "../../../firebase";
import doctorImage from "../../../assets/imgs/Dres/stock-photo-surgeon-wearing-blue-uniform-stethoscope-small.png";
import { ERROR_MESSAGES } from "../../../constants/Messages";
import { setCookie } from "../../../utils/auth/cookieSession";
import { validateEmailFormat } from "../../../utils/commons/commonFunctions";
import showToast from "../../../utils/toasts/commonToasts";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import "./LoginPage.css";
import ui from "./index.module.css";
import LandingLayout from "../../Layouts/Landing";
import { useDispatch } from "react-redux";
import {
  setCheckoutUserId,
  setUserInformation,
} from "../../../store/reducers/user/UserInformationSlice";
import { ROUTES } from "../../../constants/routes";
import { encryptPassword } from "../../../utils/auth";
import ConfirmDialogModal from "../../../components/ConfirmDialogModal";
import LinkGoogleAccountModal from "../../../components/Auth/LinkGoogleAccountModal";
import { FaFacebook, FaApple } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";

const LoginPage = () => {
  setTimeout(() => {
    const items = document.querySelectorAll(".reveal-load");
    items.forEach((item) => item.classList.add("active"));
  }, 100);

  return (
    <LandingLayout page="login" footer={false}>
      <div className={ui.login}>
        <div className={ui.loginContainer}>
          <div className={ui.subContainer}>
            <div className={ui.containerHead}>
              <h1 className="title fade-in-title">
                <span className={ui.containerTitle}>¡Bienvenido</span>
                <span className={ui.containerTitle}>de</span>
                <span className={ui.containerTitle}>nuevo!</span>
              </h1>
            </div>
            <div className="container-body">
              <FormLogin />
            </div>
          </div>
          <div className={ui.imageContainer}>
            <img src={doctorImage} alt="doctor-pic" />
          </div>
          <div className={ui.triangle}></div>
        </div>
      </div>
    </LandingLayout>
  );
};

const FormLogin = () => {
  const [userEmail, setEmail] = useState("");
  const [userPass, setPass] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [isFacebookSubmitting, setIsFacebookSubmitting] = useState(false);
  const [isAppleSubmitting, setIsAppleSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionConflictSource, setSessionConflictSource] = useState(null);
  const pendingSocialFirebaseTokenRef = useRef(null);
  const pendingSocialFirebaseEmailRef = useRef(null);
  const [linkGoogleModalOpen, setLinkGoogleModalOpen] = useState(false);
  const [linkGoogleEmail, setLinkGoogleEmail] = useState("");
  const [linkModalProviderName, setLinkModalProviderName] = useState("Google");
  const linkPendingFirebaseTokenRef = useRef(null);

  const completeSessionAfterAuth = (rest) => {
    if (!rest.has_payments && rest.user_role_id !== 4) {
      showToast.info("No haz realizado tu pago aún, procede a realizarlo");
      dispatch(setCheckoutUserId(rest.user_id));
      navigate(ROUTES.CHECKOUT);
    } else {
      dispatch(setUserInformation(rest));
      setCookie("accessToken", rest.auth_token);
      setTimeout(() => {
        window.location.href = ROUTES.PLATAFORMA_DASHBOARD;
      }, 100);
    }
  };

  const handleAccept = () => {
    setIsSubmitting(true);
    if (
      (sessionConflictSource === "google" ||
        sessionConflictSource === "facebook" ||
        sessionConflictSource === "apple") &&
      pendingSocialFirebaseTokenRef.current
    ) {
      loginUser({
        firebase_token: pendingSocialFirebaseTokenRef.current,
        environment: "platform",
        ...(pendingSocialFirebaseEmailRef.current
          ? { user_email: pendingSocialFirebaseEmailRef.current }
          : {}),
      })
        .then((res) => {
          const { status_Message, ...rest } = res.data;
          if (status_Message === "valid user") {
            completeSessionAfterAuth(rest);
          } else {
            showToast.error(
              "No se pudo cerrar la otra sesión. Intenta de nuevo.",
            );
          }
        })
        .catch(() => {
          showToast.error(
            "Hubo un error al cerrar la otra sesión, intenta nuevamente",
          );
        })
        .finally(() => {
          setIsSubmitting(false);
          setIsModalOpen(false);
          setSessionConflictSource(null);
          pendingSocialFirebaseTokenRef.current = null;
          pendingSocialFirebaseEmailRef.current = null;
        });
      return;
    }

    closeUserRemoteSession({
      user_email: userEmail,
      user_password: encryptPassword(userPass),
      environment: "platform",
    })
      .then((res) => {
        const { status_Message, ...rest } = res.data;
        if (!rest.has_payments && rest.user_role_id !== 4) {
          showToast.info("No haz realizado tu pago aún, procede a realizarlo");
          dispatch(setCheckoutUserId(rest.user_id));
          navigate(ROUTES.CHECKOUT);
        } else {
          dispatch(setUserInformation(rest));
          setCookie("accessToken", rest.auth_token);
          navigate(ROUTES.PLATAFORMA_DASHBOARD, { replace: true });
        }
      })
      .catch(() => {
        showToast.error(
          "Hubo un error al cerrar la otra sesión, intenta nuevamente",
        );
        setIsModalOpen(false);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSessionConflictSource(null);
    pendingSocialFirebaseTokenRef.current = null;
    pendingSocialFirebaseEmailRef.current = null;
  };

  const closeLinkGoogleModal = () => {
    setLinkGoogleModalOpen(false);
    setLinkGoogleEmail("");
    setLinkModalProviderName("Google");
    linkPendingFirebaseTokenRef.current = null;
    signOutFirebaseAuth().catch(() => { });
  };

  const handleLinkGoogleSuccess = (sessionRest) => {
    setLinkGoogleModalOpen(false);
    setLinkGoogleEmail("");
    setLinkModalProviderName("Google");
    linkPendingFirebaseTokenRef.current = null;
    completeSessionAfterAuth(sessionRest);
  };

  const toggleShowPassword = () => setShowPassword(!showPassword);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = () => {
    const isValidEmail = validateEmailFormat(userEmail);

    if (isValidEmail && userPass) {
      setIsSubmitting(true); // Activar el spinner
      loginUser({
        new_user_email: userEmail,
        new_user_password: encryptPassword(userPass),
        environment: "platform",
      })
        .then((res) => {
          if (res.data.status_Message === "user logged") {
            setSessionConflictSource("email");
            setIsModalOpen(true);
          }
          if (res.data.status_Message === "valid user") {
            const { status_Message, ...rest } = res.data;

            if (!rest.has_payments && rest.user_role_id !== 4) {
              showToast.info(
                "No haz realizado tu pago aún, procede a realizarlo",
              );
              dispatch(setCheckoutUserId(rest.user_id));
              navigate(ROUTES.CHECKOUT);
            } else {
              dispatch(setUserInformation(rest));

              setCookie("accessToken", rest.auth_token);

              setTimeout(() => {
                window.location.href = ROUTES.PLATAFORMA_DASHBOARD;
              }, 100);
            }
          }
          if (res.data.status_Message === "invalid user") {
            showToast.error("El usuario o la contraseña son incorrectos");
          }
          if (res.data.status_Message === "problems with last session") {
            showToast.error(
              "Hubo un problema al actualizar tu sesión. Intenta de nuevo.",
            );
          }
          if (res.data.status_Message === "problems with last jwt") {
            showToast.error(
              "Hubo un problema con la sesión. Inicia sesión de nuevo.",
            );
          }
        })
        .catch((err) => {
          const data = err.response?.data;
          if (data?.status_Message === "Firebase email not verified") {
            showToast.error("Verifica tu correo en Google antes de continuar.");
            return;
          }
          if (data?.status_Message === "Firebase token without sub/uid") {
            showToast.error(
              "No pudimos validar tu cuenta de Google. Intenta de nuevo.",
            );
            return;
          }
          const error = data?.message || "Error desconocido";
          showToast.error(ERROR_MESSAGES[error] || error);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      setEmailError(!isValidEmail);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleSubmitting(true);
    try {
      const { idToken, resolvedEmail } = await signInWithGoogleAndGetIdToken();
      if (!resolvedEmail) {
        showToast.error(
          "Google no devolvió un correo para esta cuenta. Prueba iniciar sesión con correo y contraseña o con otra cuenta de Google.",
        );
        await signOutFirebaseAuth();
        return;
      }
      const res = await loginUser({
        firebase_token: idToken,
        environment: "platform",
        user_email: resolvedEmail,
      });
      const { status_Message, ...rest } = res.data;

      if (status_Message === "user logged") {
        pendingSocialFirebaseTokenRef.current = idToken;
        pendingSocialFirebaseEmailRef.current = resolvedEmail || null;
        setSessionConflictSource("google");
        setIsModalOpen(true);
        return;
      }
      if (status_Message === "valid user") {
        completeSessionAfterAuth(rest);
        return;
      }

      if (status_Message === "need to link") {
        const email = res.data.user_email || "";
        linkPendingFirebaseTokenRef.current = idToken;
        setLinkGoogleEmail(email);
        setLinkModalProviderName("Google");
        setLinkGoogleModalOpen(true);
        return;
      }
      if (status_Message === "invalid user") {
        showToast.error(
          "No encontramos una cuenta con este correo. Regístrate primero.",
        );
        await signOutFirebaseAuth();
      }
    } catch (err) {
      const code = err?.code;
      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request"
      ) {
        return;
      }
      const data = err.response?.data;
      if (data?.status_Message === "Firebase email not verified") {
        showToast.error("Verifica tu correo en Google antes de continuar.");
      } else if (data?.status_Message === "Firebase token without sub/uid") {
        showToast.error(
          "No pudimos validar tu cuenta de Google. Intenta de nuevo.",
        );
      } else if (
        data?.status_Message === "Unsupported Firebase sign-in provider" ||
        data?.status_Message === "provider not found"
      ) {
        showToast.error(
          "El inicio con Google no está disponible aún. Contacta al administrador.",
        );
      } else if (err?.response?.status === 404) {
        showToast.error(
          "El inicio con Google no está disponible aún. Contacta al administrador.",
        );
      } else {
        const msg = data?.message || data?.status_Message || err.message;
        showToast.error(
          ERROR_MESSAGES[msg] || msg || "Error al iniciar sesión con Google",
        );
      }
      await signOutFirebaseAuth();
    } finally {
      setIsGoogleSubmitting(false);
    }
  };

  const handleFacebookLogin = async () => {
    setIsFacebookSubmitting(true);
    try {
      const { idToken } = await signInWithFacebookAndGetIdToken();
      const res = await loginUser({
        firebase_token: idToken,
        environment: "platform",
      });
      const { status_Message, ...rest } = res.data;

      if (status_Message === "user logged") {
        pendingSocialFirebaseTokenRef.current = idToken;
        pendingSocialFirebaseEmailRef.current = null;
        setSessionConflictSource("facebook");
        setIsModalOpen(true);
        return;
      }
      if (status_Message === "valid user") {
        completeSessionAfterAuth(rest);
        return;
      }
      if (status_Message === "need to link") {
        const email = res.data.user_email || "";
        linkPendingFirebaseTokenRef.current = idToken;
        setLinkGoogleEmail(email);
        setLinkModalProviderName("Facebook");
        setLinkGoogleModalOpen(true);
        return;
      }
      if (status_Message === "invalid user") {
        showToast.error(
          "No encontramos una cuenta con este correo. Regístrate primero.",
        );
        await signOutFirebaseAuth();
      }
    } catch (err) {
      const code = err?.code;
      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request"
      ) {
        return;
      }
      const data = err.response?.data;
      if (data?.status_Message === "Firebase email not verified") {
        showToast.error("Verifica tu correo en Facebook antes de continuar.");
      } else if (data?.status_Message === "Firebase token without sub/uid") {
        showToast.error(
          "No pudimos validar tu cuenta de Facebook. Intenta de nuevo.",
        );
      } else if (
        data?.status_Message === "Unsupported Firebase sign-in provider" ||
        data?.status_Message === "provider not found"
      ) {
        showToast.error(
          "El inicio con Facebook no está disponible aún. Contacta al administrador.",
        );
      } else if (err?.response?.status === 404) {
        showToast.error(
          "El inicio con Facebook no está disponible aún. Contacta al administrador.",
        );
      } else {
        const msg = data?.message || data?.status_Message || err.message;
        showToast.error(
          ERROR_MESSAGES[msg] || msg || "Error al iniciar sesión con Facebook",
        );
      }
      await signOutFirebaseAuth();
    } finally {
      setIsFacebookSubmitting(false);
    }
  };

  const handleAppleLogin = async () => {
    setIsAppleSubmitting(true);
    try {
      const { idToken, resolvedEmail } = await signInWithAppleAndGetIdToken();
      const res = await loginUser({
        firebase_token: idToken,
        environment: "platform",
        ...(resolvedEmail ? { user_email: resolvedEmail } : {}),
      });
      const { status_Message, ...rest } = res.data;

      if (status_Message === "user logged") {
        pendingSocialFirebaseTokenRef.current = idToken;
        pendingSocialFirebaseEmailRef.current = resolvedEmail || null;
        setSessionConflictSource("apple");
        setIsModalOpen(true);
        return;
      }
      if (status_Message === "valid user") {
        completeSessionAfterAuth(rest);
        return;
      }
      if (status_Message === "need to link") {
        const email = res.data.user_email || "";
        linkPendingFirebaseTokenRef.current = idToken;
        setLinkGoogleEmail(email);
        setLinkModalProviderName("Apple");
        setLinkGoogleModalOpen(true);
        return;
      }
      if (status_Message === "invalid user") {
        showToast.error(
          "No encontramos una cuenta con este correo. Regístrate primero.",
        );
        await signOutFirebaseAuth();
      }
    } catch (err) {
      const code = err?.code;
      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request"
      ) {
        return;
      }
      const data = err.response?.data;
      if (data?.status_Message === "Firebase email not verified") {
        showToast.error("Verifica tu correo en Apple antes de continuar.");
      } else if (data?.status_Message === "Firebase token without sub/uid") {
        showToast.error(
          "No pudimos validar tu cuenta de Apple. Intenta de nuevo.",
        );
      } else if (
        data?.status_Message === "Unsupported Firebase sign-in provider" ||
        data?.status_Message === "provider not found"
      ) {
        showToast.error(
          "El inicio con Apple no está disponible aún. Contacta al administrador.",
        );
      } else if (err?.response?.status === 404) {
        showToast.error(
          "El inicio con Apple no está disponible aún. Contacta al administrador.",
        );
      } else {
        const msg = data?.message || data?.status_Message || err.message;
        showToast.error(
          ERROR_MESSAGES[msg] || msg || "Error al iniciar sesión con Apple",
        );
      }
      await signOutFirebaseAuth();
    } finally {
      setIsAppleSubmitting(false);
    }
  };

  return (
    <>
      <div className="form-container reveal-load">
        <div className={ui.formGroup}>
          <label className={ui.formLabel} htmlFor="form-user">
            Correo electrónico*
          </label>
          <input
            type="text"
            name="user"
            id="form-user"
            placeholder="maria.garcia@correo.com"
            autoComplete="email"
            onChange={(e) => {
              setEmail(e.currentTarget.value);
              setEmailError(false);
            }}
            onKeyDown={handleKeyDown}
          />
          {emailError && (
            <span className={`${ui.formLabel} red`}>
              Introduce un correo válido
            </span>
          )}
        </div>
        <div className={ui.formGroup}>
          <label className={ui.formLabel} htmlFor="form-password">
            Contraseña*
          </label>
          <div className="password-input">
            <input
              type={showPassword ? "text" : "password"}
              id="form-password"
              placeholder="*******"
              autoComplete="current-password"
              value={userPass}
              onChange={(e) => setPass(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="password-login-icon" onClick={toggleShowPassword}>
              {showPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
            </div>
          </div>
          <div className={ui.subGroup}>
            <Link
              className={`${ui.linkLabel} sky-blue no-style`}
              to={ROUTES.FORGOT_PASSWORD}
            >
              Olvidé mi contraseña
            </Link>
          </div>
        </div>
        <button
          className={`button-rounded-blue-48 ${isSubmitting ? "button-rounded-blue-48--disabled" : ""
            } flex items-center justify-center gap-2`}
          type="button"
          style={{ marginTop: "20px" }}
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            isGoogleSubmitting ||
            isFacebookSubmitting ||
            isAppleSubmitting ||
            !validateEmailFormat(userEmail) ||
            !userPass
          }
        >
          {isSubmitting ? (
            <>
              <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-white"></div>
              <span className="button-text">Iniciando Sesión</span>
            </>
          ) : (
            <span className="button-text">Iniciar Sesión</span>
          )}
        </button>
        <p
          className="flex-row-nw jc-center gap-8"
          style={{ marginTop: "1rem" }}
        >
          <span className={ui.linkLabel}>¿Aun no eres miembro?</span>
          <Link className={`${ui.linkLabel} sky-blue no-style`} to="/registro">
            Registrate Ahora
          </Link>
        </p>
        <div className={ui.dividerRow}>
          <span>o</span>
        </div>
        <button
          type="button"
          className={`${ui.googleButton} ${isGoogleSubmitting ? ui.googleButtonBusy : ""
            }`}
          onClick={handleGoogleLogin}
          disabled={
            isSubmitting ||
            isGoogleSubmitting ||
            isFacebookSubmitting ||
            isAppleSubmitting
          }
          aria-busy={isGoogleSubmitting}
        >
          {isGoogleSubmitting ? (
            <>
              <div
                className="spinner-border animate-spin inline-block w-4 h-4 shrink-0 border-2 rounded-full border-[#05B2FA] border-t-transparent"
                aria-hidden
              />
              <span className="button-text">Iniciando sesión</span>
            </>
          ) : (
            <>
              <FcGoogle size={22} className="shrink-0" />
              <span className="button-text">Iniciar sesión con Google</span>
            </>
          )}
        </button>
        <button
          type="button"
          className={`${ui.appleButton} ${isAppleSubmitting ? ui.appleButtonBusy : ""
            }`}
          onClick={handleAppleLogin}
          disabled={
            isSubmitting ||
            isGoogleSubmitting ||
            isFacebookSubmitting ||
            isAppleSubmitting
          }
          aria-busy={isAppleSubmitting}
        >
          {isAppleSubmitting ? (
            <>
              <div
                className="spinner-border animate-spin inline-block w-4 h-4 shrink-0 border-2 rounded-full border-white border-t-transparent"
                aria-hidden
              />
              <span className="button-text">Iniciando sesión</span>
            </>
          ) : (
            <>
              <FaApple size={22} className="shrink-0" aria-hidden />
              <span className="button-text">Iniciar sesión con Apple</span>
            </>
          )}
        </button>
        {/* <button
          type="button"
          className={`${ui.facebookButton} ${
            isFacebookSubmitting ? ui.facebookButtonBusy : ""
          }`}
          onClick={handleFacebookLogin}
          disabled={isSubmitting || isGoogleSubmitting || isFacebookSubmitting}
          aria-busy={isFacebookSubmitting}
        >
          {isFacebookSubmitting ? (
            <>
              <div
                className="spinner-border animate-spin inline-block w-4 h-4 shrink-0 border-2 rounded-full border-white border-t-transparent"
                aria-hidden
              />
              <span className="button-text">Iniciando sesión</span>
            </>
          ) : (
            <>
              <FaFacebook size={22} className="shrink-0" aria-hidden />
              <span className="button-text">Iniciar sesión con Facebook</span>
            </>
          )}
        </button> */}
      </div>
      <ConfirmDialogModal
        isOpen={isModalOpen}
        onAccept={handleAccept}
        onCancel={handleCancel}
        title="Cierre de sesión"
        description="Hemos detectado una sesión activa, al dar continuar, esta será cerrada automáticamente"
      />
      <LinkGoogleAccountModal
        isOpen={linkGoogleModalOpen}
        onClose={closeLinkGoogleModal}
        enarmEmail={linkGoogleEmail}
        firebaseIdTokenRef={linkPendingFirebaseTokenRef}
        onLinkedSuccess={handleLinkGoogleSuccess}
        signOutFirebaseAuth={signOutFirebaseAuth}
        providerDisplayName={linkModalProviderName}
      />
    </>
  );
};

export default LoginPage;
